<template>
  <main class="history-export" data-test="history-export-page">
    <header class="history-export__title">
      <div>
        <span class="history-export__eyebrow">{{ $t('license.history_export.limited_access') }}</span>
        <h1 ref="heading" tabindex="-1">{{ pageTitle }}</h1>
        <p>{{ $t('license.history_export.description') }}</p>
      </div>
      <v-btn
        v-if="license.effective_status !== 'suspended'"
        flat
        class="history-export__back"
        data-test="history-export-back"
        @click="$router.push('/home')"
      >
        {{ $t('license.history_export.back') }}
      </v-btn>
    </header>

    <historical-export-filters ref="filters" :loading="loading" @search="search" />

    <div v-if="offline" class="history-export__offline" role="status" data-test="history-export-offline">
      <v-icon small>cloud_off</v-icon>{{ $t('license.history_export.errors.offline') }}
    </div>
    <div v-if="error" class="history-export__state history-export__state--error" role="alert" data-test="history-export-error">
      <v-icon color="error">error_outline</v-icon>
      <span>{{ $t(`license.history_export.errors.${error}`) }}</span>
      <v-btn flat data-test="history-export-error-retry" @click="search(lastFilters)">{{ $t('license.history_export.retry') }}</v-btn>
    </div>
    <div v-if="loading" class="history-export__loading" role="status" data-test="history-export-loading">
      <div class="history-export__loading-label">
        <v-progress-circular indeterminate color="primary" />
        <span>{{ $t('license.history_export.loading') }}</span>
      </div>
      <div v-for="row in 3" :key="row" class="history-export__skeleton" aria-hidden="true" />
    </div>
    <div v-else-if="!error && searched && items.length === 0" class="history-export__state" data-test="history-export-empty">
      <v-icon>inventory_2</v-icon>
      <span>{{ $t(hasFilters ? 'license.history_export.empty_filtered' : 'license.history_export.empty_all') }}</span>
      <v-btn v-if="hasFilters" flat data-test="history-export-empty-clear" @click="clearFilters">
        {{ $t('license.history_export.clear') }}
      </v-btn>
    </div>
    <historical-export-list
      v-else-if="items.length"
      :items="items"
      :selected="selected"
      :errors="artifactErrors"
      :offline="offline"
      @toggle="toggle"
      @download="download"
    />

    <footer class="history-export__selection">
      <span id="history-export-zip-state">{{ zipState }}</span>
      <v-btn
        color="primary"
        :loading="archiving"
        :disabled="zipDisabled"
        :aria-disabled="zipDisabled ? 'true' : 'false'"
        aria-describedby="history-export-zip-state"
        data-test="history-export-download-zip"
        @click="archive"
      >
        {{ $t('license.history_export.download_zip') }}
      </v-btn>
    </footer>
  </main>
</template>

<script>
import { mapGetters } from 'vuex'
import HistoricalExportFilters from '@/components/history-export/HistoricalExportFilters.vue'
import HistoricalExportList from '@/components/history-export/HistoricalExportList.vue'
import { historyExportServices } from '@/services/license'

export default {
  components: { HistoricalExportFilters, HistoricalExportList },
  data: () => ({ items: [], selected: [], loading: false, archiving: false, searched: false, error: null, artifactErrors: {}, lastFilters: {}, online: true }),
  computed: {
    ...mapGetters({ license: 'getLicense' }),
    offline() { return !this.online },
    hasFilters() { return Object.values(this.lastFilters).some(value => Boolean(value)) },
    pageTitle() {
      const key = this.license.effective_status === 'suspended'
        ? 'license.history_export.suspended_title'
        : 'license.history_export.title'
      return this.$t(key)
    },
    zipDisabled() { return this.selected.length === 0 || this.offline },
    zipState() {
      if (this.offline) return this.$t('license.history_export.zip_disabled_offline')
      if (this.selected.length === 0) return this.$t('license.history_export.zip_disabled_empty')
      return this.$t('license.history_export.selected', { count: this.selected.length })
    },
  },
  methods: {
    saveBlob(blob, filename) {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    },
    async search(filters = {}) {
      this.loading = true
      this.error = null
      this.lastFilters = filters
      try {
        this.items = await historyExportServices.search(filters)
        this.selected = []
        this.searched = true
      } catch (_) {
        this.error = navigator.onLine ? 'server' : 'offline'
      } finally {
        this.loading = false
      }
    },
    clearFilters() { this.$refs.filters.clear() },
    toggle(id) {
      this.selected = this.selected.includes(id) ? this.selected.filter(value => value !== id) : [...this.selected, id]
    },
    async download(artifact) {
      try {
        this.$delete(this.artifactErrors, artifact.id)
        const response = await fetch(historyExportServices.downloadUrl(artifact.id), { credentials: 'same-origin' })
        if (response.status === 410) throw new Error('expired')
        if (!response.ok) throw new Error('server')
        this.saveBlob(await response.blob(), artifact.filename)
      } catch (error) {
        this.$set(this.artifactErrors, artifact.id, error.message === 'expired' ? 'expired' : (navigator.onLine ? 'server' : 'offline'))
      }
    },
    async archive() {
      if (this.zipDisabled) return
      this.archiving = true
      this.error = null
      try {
        // 交給瀏覽器原生下載管線，避免把最多 250MiB ZIP 緩衝在頁面記憶體。
        await historyExportServices.archive(this.selected)
      } catch (error) {
        this.error = !navigator.onLine ? 'offline' : (error.response && error.response.status === 410 ? 'expired' : 'server')
      } finally {
        this.archiving = false
      }
    },
  },
  created() {
    this.online = navigator.onLine
    this.onConnectivityChange = () => { this.online = navigator.onLine }
    window.addEventListener('online', this.onConnectivityChange)
    window.addEventListener('offline', this.onConnectivityChange)
    this.search({})
  },
  mounted() { this.$nextTick(() => this.$refs.heading && this.$refs.heading.focus()) },
  beforeDestroy() {
    window.removeEventListener('online', this.onConnectivityChange)
    window.removeEventListener('offline', this.onConnectivityChange)
  },
}
</script>

<style lang="scss" scoped>
.history-export { width: 100%; max-width: 1180px; margin: 0 auto; padding: 28px 24px 32px; align-self: flex-start; display: grid; grid-template-columns: minmax(0, 1fr) minmax(240px, 340px); column-gap: 20px; }
.history-export > :not(.history-export__title):not(.history-export__selection) { grid-column: 1 / -1; }
.history-export__title { grid-column: 1; grid-row: 1; display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; margin-bottom: 20px; }
.history-export__title h1 { margin: 0 0 4px; font-size: 28px; font-weight: 500; }
.history-export__title p { margin: 0; color: var(--evas-text-label); }
.history-export__eyebrow { display: block; margin-bottom: 4px; color: #b71c1c; font-size: 12px; font-weight: 700; letter-spacing: .08em; }
.history-export__offline { display: flex; gap: 8px; align-items: center; margin: 12px 0; padding: 10px 12px; background: #fff3e0; color: #7f3b00; }
.history-export h1:focus { outline: 3px solid var(--evas-accent-bright); outline-offset: 4px; }
.history-export__state { min-height: 180px; display: flex; align-items: center; justify-content: center; gap: 12px; text-align: center; }
.history-export__state--error { color: #b71c1c; }
.history-export__back,
.history-export__state .v-btn { min-width: 44px; min-height: 44px; }
.history-export__loading { min-height: 260px; padding: 28px 0; }
.history-export__loading-label { display: flex; align-items: center; justify-content: center; gap: 12px; margin-bottom: 20px; }
.history-export__skeleton { height: 58px; margin: 10px 0; border-radius: 4px; background: linear-gradient(90deg, #eceff1 25%, #f8f9fa 50%, #eceff1 75%); background-size: 200% 100%; animation: history-export-pulse 1.4s ease-in-out infinite; }
.history-export__selection { grid-column: 2; grid-row: 1; align-self: start; display: flex; align-items: center; justify-content: flex-end; gap: 16px; min-height: 52px; padding: 4px 0; color: var(--evas-text); }
.history-export__selection .v-btn { min-width: 132px; min-height: 44px; margin: 0; }
@keyframes history-export-pulse { from { background-position: 100% 0; } to { background-position: -100% 0; } }
@media (prefers-reduced-motion: reduce) { .history-export__skeleton { animation: none; } }
@media (max-width: 600px) {
  .history-export { display: flex; flex-direction: column; padding: 20px 16px 16px; }
  .history-export__title { display: grid; }
  .history-export__selection { position: sticky; right: auto; left: auto; bottom: 16px; order: 10; flex-direction: column; align-items: stretch; gap: 8px; width: 100%; margin-top: 16px; padding: 12px 16px; background: #263238; color: #fff; border-radius: 4px; box-shadow: 0 4px 16px rgba(0,0,0,.28); z-index: 6; }
  .history-export__selection .v-btn { width: 100%; }
}
</style>
